import { useState } from "react"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Bot, User, MessageSquareOff, MessageCircle, Settings, Copy, Check, Wrench, CheckCircle2, XCircle, ArrowRight } from "lucide-react"
import { Call, CallMessage } from "@/api/calls/calls.types"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

interface ConversationTabProps {
    call: Call
}

function formatTimestamp(time: number): string {
    const date = new Date(time);
    return new Intl.DateTimeFormat('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
    }).format(date);
}

function formatSecondsFromStart(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// Parse tool call arguments safely
function parseToolArguments(args: string): Record<string, unknown> | null {
    try {
        const parsed = JSON.parse(args);
        return Object.keys(parsed).length > 0 ? parsed : null;
    } catch {
        return null;
    }
}

// Component to render tool calls
function ToolCallMessage({ message }: { message: CallMessage }) {
    const [expanded, setExpanded] = useState(false);
    
    if (!message.toolCalls || message.toolCalls.length === 0) return null;

    return (
        <div className="flex justify-center my-3">
            <div className="w-full max-w-[85%]">
                <div className="flex items-center gap-2 mb-2">
                    <div className="h-px flex-1 bg-border" />
                    <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                        <Wrench className="h-3 w-3" />
                        Tool Call
                    </span>
                    <div className="h-px flex-1 bg-border" />
                </div>
                <div className="space-y-2">
                    {message.toolCalls.map((toolCall, tcIndex) => {
                        const args = parseToolArguments(toolCall.function.arguments);
                        return (
                            <div 
                                key={toolCall.id || tcIndex}
                                className="rounded-lg border bg-gradient-to-r from-amber-50/50 to-orange-50/50 dark:from-amber-950/20 dark:to-orange-950/20 border-amber-200/50 dark:border-amber-800/30 overflow-hidden"
                            >
                                <div 
                                    className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-amber-100/30 dark:hover:bg-amber-900/20 transition-colors"
                                    onClick={() => setExpanded(!expanded)}
                                >
                                    <div className="flex items-center gap-2">
                                        <div className="flex h-6 w-6 items-center justify-center rounded-md bg-amber-100 dark:bg-amber-900/50">
                                            <Wrench className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                                        </div>
                                        <span className="text-sm font-medium text-amber-800 dark:text-amber-300">
                                            {toolCall.function.name}
                                        </span>
                                        <Badge variant="outline" className="text-[10px] h-5 px-1.5 bg-amber-100/50 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400">
                                            {toolCall.type}
                                        </Badge>
                                    </div>
                                    <span className="text-xs text-muted-foreground">
                                        {formatSecondsFromStart(message.secondsFromStart)}
                                    </span>
                                </div>
                                {expanded && args && (
                                    <div className="px-3 pb-2 pt-1 border-t border-amber-200/30 dark:border-amber-800/20">
                                        <div className="text-xs text-muted-foreground mb-1">Arguments:</div>
                                        <pre className="text-xs bg-white/50 dark:bg-black/20 rounded p-2 overflow-x-auto">
                                            {JSON.stringify(args, null, 2)}
                                        </pre>
                                    </div>
                                )}
                                {expanded && !args && (
                                    <div className="px-3 pb-2 pt-1 border-t border-amber-200/30 dark:border-amber-800/20">
                                        <span className="text-xs text-muted-foreground italic">No arguments</span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

// Helper function to safely convert result to string
function getResultString(result: unknown): string {
    if (!result) return '';
    if (typeof result === 'string') return result;
    try {
        return JSON.stringify(result, null, 2);
    } catch {
        return '[Complex data]';
    }
}

// Component to render tool call results
function ToolResultMessage({ message }: { message: CallMessage }) {
    const [expanded, setExpanded] = useState(false);
    const resultString = getResultString(message.result);
    const isSuccess = resultString && resultString !== "No result returned." && !resultString.toLowerCase().includes('error');
    
    return (
        <div className="flex justify-center my-3">
            <div className="w-full max-w-[85%]">
                <div className="flex items-center gap-2 mb-2">
                    <div className="h-px flex-1 bg-border" />
                    <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                        <ArrowRight className="h-3 w-3" />
                        Tool Result
                    </span>
                    <div className="h-px flex-1 bg-border" />
                </div>
                <div 
                    className={cn(
                        "rounded-lg border overflow-hidden",
                        isSuccess 
                            ? "bg-gradient-to-r from-emerald-50/50 to-green-50/50 dark:from-emerald-950/20 dark:to-green-950/20 border-emerald-200/50 dark:border-emerald-800/30"
                            : "bg-gradient-to-r from-red-50/50 to-rose-50/50 dark:from-red-950/20 dark:to-rose-950/20 border-red-200/50 dark:border-red-800/30"
                    )}
                >
                    <div 
                        className={cn(
                            "flex items-center justify-between px-3 py-2 cursor-pointer transition-colors",
                            isSuccess 
                                ? "hover:bg-emerald-100/30 dark:hover:bg-emerald-900/20"
                                : "hover:bg-red-100/30 dark:hover:bg-red-900/20"
                        )}
                        onClick={() => setExpanded(!expanded)}
                    >
                        <div className="flex items-center gap-2">
                            <div className={cn(
                                "flex h-6 w-6 items-center justify-center rounded-md",
                                isSuccess 
                                    ? "bg-emerald-100 dark:bg-emerald-900/50"
                                    : "bg-red-100 dark:bg-red-900/50"
                            )}>
                                {isSuccess ? (
                                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                                ) : (
                                    <XCircle className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
                                )}
                            </div>
                            <span className={cn(
                                "text-sm font-medium",
                                isSuccess 
                                    ? "text-emerald-800 dark:text-emerald-300"
                                    : "text-red-800 dark:text-red-300"
                            )}>
                                {message.name || 'Function Result'}
                            </span>
                            <Badge 
                                variant="outline" 
                                className={cn(
                                    "text-[10px] h-5 px-1.5",
                                    isSuccess
                                        ? "bg-emerald-100/50 dark:bg-emerald-900/30 border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400"
                                        : "bg-red-100/50 dark:bg-red-900/30 border-red-300 dark:border-red-700 text-red-700 dark:text-red-400"
                                )}
                            >
                                {isSuccess ? 'completed' : 'no result'}
                            </Badge>
                        </div>
                        <span className="text-xs text-muted-foreground">
                            {formatSecondsFromStart(message.secondsFromStart)}
                        </span>
                    </div>
                    {expanded && (
                        <div className={cn(
                            "px-3 pb-2 pt-1 border-t",
                            isSuccess 
                                ? "border-emerald-200/30 dark:border-emerald-800/20"
                                : "border-red-200/30 dark:border-red-800/20"
                        )}>
                            <div className="text-xs text-muted-foreground mb-1">Result:</div>
                            <pre className="text-xs bg-white/50 dark:bg-black/20 rounded p-2 overflow-x-auto whitespace-pre-wrap break-words">
                                {resultString || 'No result'}
                            </pre>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export function ConversationTab({ call }: ConversationTabProps) {
    const systemMessages = call.messages?.filter(m => m.role === 'system') || [];
    const conversationMessages = call.messages?.filter(m => m.role !== 'system') || [];
    const chatMessages = conversationMessages.filter(m => m.role === 'bot' || m.role === 'user');
    const totalMessages = chatMessages.length;
    const [copiedMessageId, setCopiedMessageId] = useState<number | null>(null);
    const [hoveredMessageId, setHoveredMessageId] = useState<number | null>(null);

    const copyToClipboard = (text: string, messageId: number) => {
        navigator.clipboard.writeText(text);
        setCopiedMessageId(messageId);
        setTimeout(() => setCopiedMessageId(null), 2000);
    };

    // Early return if no messages
    if (!call?.messages?.length) {
        return (
            <Card className="shadow-sm">
                <CardHeader className="border-b bg-muted/20">
                    <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                            <MessageCircle className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                            <CardTitle className="text-base">Conversation</CardTitle>
                            <CardDescription className="text-xs text-muted-foreground/70">
                                No messages available
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="p-6">
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                            <MessageSquareOff className="h-8 w-8 text-muted-foreground" />
                        </div>
                        <h3 className="text-base font-semibold mb-2">No Messages Available</h3>
                        <p className="text-sm text-muted-foreground max-w-sm">
                            There are no conversation messages available for this call yet.
                        </p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="shadow-sm">
            <CardHeader className="border-b bg-muted/20">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                            <MessageCircle className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                            <CardTitle className="text-base">Conversation</CardTitle>
                            <CardDescription className="text-xs text-muted-foreground/70">
                                {totalMessages} {totalMessages === 1 ? 'message' : 'messages'} exchanged
                            </CardDescription>
                        </div>
                    </div>
                    {systemMessages.length > 0 && (
                        <Badge variant="outline" className="gap-1.5">
                            <Settings className="h-3 w-3" />
                            System Config
                        </Badge>
                    )}
                </div>
            </CardHeader>
            <CardContent className="p-6">
                <ScrollArea className="h-[35rem] pr-4">
                    <div className="space-y-4">
                        {/* Collapsible System Message */}
                        {systemMessages.length > 0 && systemMessages.map((message, index) => (
                            <details key={`system-${index}`} className="group mb-4">
                                <summary className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50 text-sm font-medium cursor-pointer hover:bg-muted transition-colors">
                                    <Settings className="h-4 w-4 text-muted-foreground" />
                                    <span>System Configuration</span>
                                </summary>
                                <div className="mt-3 ml-3 pl-4 text-sm text-muted-foreground whitespace-pre-wrap border-l-2 border-primary/20">
                                    {message.message}
                                </div>
                            </details>
                        ))}

                        {/* Conversation messages */}
                        {conversationMessages.map((message, index) => {
                            // Handle tool calls
                            if (message.role === 'tool_calls') {
                                return <ToolCallMessage key={`tool-call-${index}`} message={message} />;
                            }

                            // Handle tool call results
                            if (message.role === 'tool_call_result') {
                                return <ToolResultMessage key={`tool-result-${index}`} message={message} />;
                            }

                            // Regular bot/user messages
                            return (
                                <div
                                    key={`message-${index}`}
                                    className={`flex gap-3 ${
                                        message.role === 'bot' ? 'flex-row' : 'flex-row-reverse'
                                    }`}
                                >
                                    <Avatar className={`flex-shrink-0 h-8 w-8 ${
                                        message.role === 'bot' 
                                            ? 'bg-primary/10 text-primary' 
                                            : 'bg-muted text-foreground'
                                    }`}>
                                        <AvatarFallback>
                                            {message.role === 'bot' ? <Bot className="h-4 w-4" /> : <User className="h-4 w-4" />}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className={`flex flex-col max-w-[75%] ${
                                        message.role === 'bot' ? '' : 'items-end'
                                    }`}>
                                        <div 
                                            className={`flex items-start gap-2 ${
                                                message.role === 'bot' ? 'flex-row' : 'flex-row-reverse'
                                            }`}
                                            onMouseEnter={() => setHoveredMessageId(index)}
                                            onMouseLeave={() => setHoveredMessageId(null)}
                                        >
                                            <div className={`rounded-lg px-4 py-2.5 ${
                                                message.role === 'bot' 
                                                    ? 'bg-muted text-foreground' 
                                                    : 'bg-primary text-primary-foreground'
                                            }`}>
                                                <p className="text-sm whitespace-pre-wrap leading-relaxed break-words">{message.message}</p>
                                            </div>
                                            
                                            {/* Copy Button - Floats outside bubble */}
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className={`h-7 w-7 shrink-0 transition-opacity duration-200 hover:bg-muted ${
                                                    hoveredMessageId === index ? 'opacity-100' : 'opacity-0'
                                                }`}
                                                onClick={() => copyToClipboard(message.message, index)}
                                            >
                                                {copiedMessageId === index ? (
                                                    <Check className="h-3.5 w-3.5 text-green-500" />
                                                ) : (
                                                    <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                                                )}
                                            </Button>
                                        </div>
                                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1.5">
                                            {message.time && (
                                                <span>{formatTimestamp(message.time)}</span>
                                            )}
                                            {message.duration && (
                                                <>
                                                    <span>•</span>
                                                    <span>{(message.duration / 1000).toFixed(1)}s</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </ScrollArea>
            </CardContent>
        </Card>
    )
}
